import { EffectSources, mergeEffects, OnRunEffects } from '@ngrx/effects';
import { catchError, dematerialize, exhaustMap, filter, groupBy, map, mergeMap } from 'rxjs/operators';
import { Action, Store } from '@ngrx/store';
import { Notification, Observable, throwError } from 'rxjs';
import { ErrorHandler, Injectable, Type } from '@angular/core';
import { IEffect } from './effect.interface';
import { getSourceForInstance } from './effect-metadata';
import { verifyOutput } from './effect-notification';
import { EffectsErrorHandler } from '@ngrx/effects/src/effects_error_handler';
import { NextNotification } from '@ngrx/effects/src/utils';

export const NGRX_EFFECTS_PROPS_KEY = '__@ngrx/effects__';

export const onIdentifyEffectsKey = 'ngrxOnIdentifyEffects';
export const onRunEffectsKey = 'ngrxOnRunEffects';
export const onInitEffects = 'ngrxOnInitEffects';

function customEffectErrorHandler<T extends Action>(observable$: Observable<T>, errorHandler: ErrorHandler) {
  return observable$.pipe(
    catchError((error) => {
      errorHandler.handleError(error);
      return throwError(() => error);
    }),
  );
}
@Injectable()
export class DeletableEffectSources extends EffectSources {
  protected effectsWhiteList: string[] = [];
  protected ngStore: Store<any>;

  constructor(
    private ngErrorHandler: ErrorHandler,
    ngStore: Store<any>,
  ) {
    super(ngErrorHandler, customEffectErrorHandler);
    this.ngStore = ngStore;
  }

  addEffects(effectSourceInstance: any) {
    const name = this.getEffectId(effectSourceInstance);
    if (!this.effectsWhiteList.includes(name)) {
      this.effectsWhiteList.push(name);
      this.next(effectSourceInstance);

      if (onInitEffects in effectSourceInstance && typeof effectSourceInstance[onInitEffects] === 'function') {
        this.ngStore.dispatch(effectSourceInstance[onInitEffects]());
      }
    }
  }

  deleteEffects(effectSourceType: Type<IEffect>[]) {
    effectSourceType.forEach((effectType) => {
      this.deleteEffect(effectType);
    });
  }

  deleteEffect(effectSourceType: Type<IEffect>) {
    this.effectsWhiteList = this.effectsWhiteList.filter((effectId) => {
      const constructor = effectSourceType.prototype.constructor;
      return effectId !== constructor.name + JSON.stringify(constructor[NGRX_EFFECTS_PROPS_KEY]);
    });
  }

  // @ts-ignore
  toActions(): Observable<Action> {
    return this.pipe(
      groupBy(getSourceForInstance),
      mergeMap((source$) => source$.pipe(groupBy(effectsInstance))),
      mergeMap((source$) =>
        source$.pipe(
          exhaustMap((instance) => resolveEffectSource(instance, this.ngErrorHandler, customEffectErrorHandler)),
          filter((output) => {
            return this.effectsWhiteList.includes(this.getEffectId(output['sourceInstance']));
          }),
          map((output) => {
            verifyOutput(output, this.ngErrorHandler);

            return output.notification;
          }),
          filter((notification) => notification.kind === 'N'),
          map((notification: NextNotification<Action>) => new Notification(notification.kind, notification.value)),
          dematerialize(),
        ),
      ),
    );
  }

  getEffectId(effectSourceInstance) {
    const prototype = Object.getPrototypeOf(effectSourceInstance);
    return prototype.constructor.name + JSON.stringify(prototype.constructor[NGRX_EFFECTS_PROPS_KEY]);
  }
}

export function effectsInstance(sourceInstance: any) {
  if (onIdentifyEffectsKey in sourceInstance && typeof sourceInstance[onIdentifyEffectsKey] === 'function') {
    return sourceInstance[onIdentifyEffectsKey]();
  }

  return '';
}

export function resolveEffectSource(
  sourceInstance: any,
  globalErrorHandler: ErrorHandler,
  effectsErrorHandler: EffectsErrorHandler,
) {
  const mergedEffects$ = mergeEffects(sourceInstance, globalErrorHandler, effectsErrorHandler);

  if (isOnRunEffects(sourceInstance)) {
    return sourceInstance.ngrxOnRunEffects(mergedEffects$);
  }

  return mergedEffects$;
}

function isOnRunEffects(sourceInstance): sourceInstance is OnRunEffects {
  const source = getSourceForInstance(sourceInstance);

  return onRunEffectsKey in source && typeof source[onRunEffectsKey] === 'function';
}
